/**
 * practiceOwnerPriority — CORE-224.
 *
 * In the Live practice pickers a master should find their own practices
 * before the общая «Библиотека Agapella» of every other master. Both web
 * pickers (create-Live wizard and the in-session «Сменить медиа» modal)
 * keep their own view-models, but they share this one pure split so the
 * priority rule lives in a single, unit-tested place.
 *
 * The function is deliberately dumb: it never fetches, filters visibility,
 * or de-duplicates — it only partitions an already server-filtered, already
 * client-searched list by ownership. Visibility/moderation stay a server
 * concern (the list handed in is the one `GET /api/content/videos` already
 * scrubbed), so hidden/blocked content can never surface here.
 */

/**
 * Split `items` into the current master's practices and everyone else's,
 * preserving the incoming order inside each block.
 *
 * - `currentMasterProfileId == null` (a regular user with no master profile)
 *   ⇒ `mine` is empty and everything falls into `others`; the caller renders
 *   no «Мои практики» section.
 * - a master whose own practices aren't in the list ⇒ `mine` is empty for the
 *   same reason.
 * - the blocks are disjoint by construction, so nothing is ever shown twice.
 */
export function partitionOwnPractices<T extends { masterProfileId: number | null }>(
  items: T[],
  currentMasterProfileId: number | null | undefined,
): { mine: T[]; others: T[] } {
  // No master profile → no personal block. Keep the original list intact.
  if (currentMasterProfileId == null) {
    return { mine: [], others: [...items] };
  }
  const mine: T[] = [];
  const others: T[] = [];
  for (const it of items) {
    if (it.masterProfileId != null && it.masterProfileId === currentMasterProfileId) {
      mine.push(it);
    } else {
      others.push(it);
    }
  }
  return { mine, others };
}
